import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, RotateCcw, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const DURATIONS = [
  { id: "any", label: "Any length" },
  { id: "short", label: "Under 4 min" },
  { id: "medium", label: "4–20 min" },
  { id: "long", label: "Over 20 min" },
];

const WINDOWS = [
  { id: "any", label: "Any time" },
  { id: "24h", label: "Last 24h" },
  { id: "7d", label: "This week" },
  { id: "30d", label: "This month" },
];

const SORTS = [
  { id: "trending", label: "Trending rank" },
  { id: "velocity", label: "Fastest rising" },
  { id: "views", label: "Most viewed" },
  { id: "likes", label: "Most liked" },
  { id: "newest", label: "Newest" },
];

const VIEW_FLOORS = [
  { id: "0", label: "Any views" },
  { id: "100000", label: "100K+" },
  { id: "1000000", label: "1M+" },
  { id: "10000000", label: "10M+" },
];

/**
 * One filter dropdown.
 *
 * Uses the project's Radix select rather than a native <select>: the browser renders
 * a native option list with OS chrome (white panel, blue highlight) that cannot be
 * styled, which clashed badly with the dark theme.
 */
// Fixed 132px triggers wrapped 1/2/1 at phone widths and stood 36px tall, under a
// comfortable touch target. On small screens each one takes half the row, so the
// four filters form an even 2x2 block; sm: and up restores the inline auto widths.
const FilterSelect = ({ label, value, options, onChange, testId }) => (
  <div className="flex flex-col gap-1.5 flex-1 basis-[calc(50%-0.375rem)] min-w-0 sm:flex-none sm:basis-auto">
    <span className="text-[10px] uppercase tracking-wide text-white/40 font-medium">{label}</span>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        className="h-11 sm:h-9 w-full sm:w-auto sm:min-w-[132px] gap-2 rounded-xl border-white/10 bg-white/5 px-3 text-xs font-medium text-white/90 shadow-none transition-colors hover:border-orange-500/40 hover:bg-white/[0.07] focus:ring-1 focus:ring-orange-500/50 data-[state=open]:border-orange-500/60"
        data-testid={testId}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="rounded-xl border-white/10 bg-zinc-950/95 backdrop-blur-xl shadow-2xl">
        {options.map((o) => (
          <SelectItem
            key={o.id}
            value={o.id}
            className="cursor-pointer rounded-lg py-1.5 text-xs text-white/80 focus:bg-orange-500/15 focus:text-orange-300 data-[state=checked]:text-orange-400"
          >
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

/**
 * Duration, recency, view floor and sort.
 *
 * "Fastest rising" only means something once snapshots exist, so it is labelled
 * plainly rather than promising a ranking the data may not support yet.
 */
/**
 * Duration, recency, view floor and sort.
 *
 * Collapsed behind a single row on phones. Expanded, the four dropdowns form a
 * 2x2 block roughly 200px tall, which on a 390x780 screen pushed the first video
 * completely below the fold - a screenful of controls before any content. The
 * summary row shows how many are active so nothing is hidden silently. From sm:
 * up there is width to spare, so the block is always open.
 *
 * "Fastest rising" only means something once snapshots exist, so it is labelled
 * plainly rather than promising a ranking the data may not support yet.
 */
export const AdvancedFilters = ({ filters, setFilter, resetFilters, activeCount }) => {
  const [open, setOpen] = useState(false);

  const selects = (
    <>
      <FilterSelect
        label="Length"
        value={filters.duration}
        options={DURATIONS}
        onChange={(v) => setFilter({ duration: v })}
        testId="filter-duration"
      />
      <FilterSelect
        label="Uploaded"
        value={filters.published_within}
        options={WINDOWS}
        onChange={(v) => setFilter({ published_within: v })}
        testId="filter-published"
      />
      <FilterSelect
        label="Views"
        // Radix works in strings; the filter state keeps min_views numeric.
        value={String(filters.min_views)}
        options={VIEW_FLOORS}
        onChange={(v) => setFilter({ min_views: Number(v) })}
        testId="filter-minviews"
      />
      <FilterSelect
        label="Sort by"
        value={filters.sort_by}
        options={SORTS}
        onChange={(v) => setFilter({ sort_by: v })}
        testId="filter-sort"
      />

      {activeCount > 0 && (
        <button
          onClick={resetFilters}
          className="ml-auto mb-0.5 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-white/50 hover:text-orange-400 hover:bg-white/5 transition-colors"
          data-testid="filter-reset"
        >
          <RotateCcw className="w-3 h-3" /> Reset ({activeCount})
        </button>
      )}
    </>
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-3 sm:mb-6"
      data-testid="advanced-filters"
    >
      {/* Phone: one tappable summary row, expanding on demand. */}
      <div className="sm:hidden rounded-2xl bg-white/[0.03] border border-white/10">
        <button
          onClick={() => setOpen(!open)}
          className="flex w-full items-center gap-2 px-3 h-12 text-xs font-medium text-white/60"
          aria-expanded={open}
          data-testid="advanced-filters-toggle"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filters
          {activeCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-orange-500 text-black text-[10px] font-bold">
              {activeCount}
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 ml-auto transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap items-end gap-3 p-3 pt-0">{selects}</div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* sm and up: always open, unchanged. */}
      <div className="hidden sm:flex flex-wrap items-end gap-3 p-3 rounded-2xl bg-white/[0.03] border border-white/10">
        <div className="flex items-center gap-1.5 text-xs font-medium text-white/50 pb-2.5">
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filters
        </div>
        {selects}
      </div>
    </motion.div>
  );
};
